import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../api/api';
import { EventOrder } from '../../types';
import { getKitchenWorkload, kitchenMissingLabels, KitchenEmployee, KitchenTask, recommendKitchenAssignee, validateWorkParameters, WorkParameters, WorkPreviewItem, workStatusLabels } from '../../utils/kitchenWork';
import { getOperatingContext, isOperatingContextReady, OPERATING_GROUP_CHANGED_EVENT, OPERATING_GROUP_STORAGE_KEY, useOperatingContext } from '../../utils/operatingContextLifecycle';
import { OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE, useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';

type Station = { id: number; name: string; isArchived: boolean };
type ProducingKitchen = { id: number; name: string };
type Workspace = { canManage: boolean; canEditManagement: boolean; canEditEmploymentSettings: boolean; canManageStationCatalog: boolean; employees: KitchenEmployee[]; tasks: KitchenTask[]; stations: Station[] };
type Preview = { items: WorkPreviewItem[]; warnings: string[]; orderVersions: Record<number, number> };
type TaskHistory = { id: number; actor: string; occurredAtUtc: string; fromStatus: KitchenTask['status'] | null; toStatus: KitchenTask['status']; notes: string; completedQuantity: number | null };
type OperatingContextSnapshot = { token: string; generation: number; groupId: string };
const panel: React.CSSProperties = { padding: 16, border: '1px solid var(--crm-border)', borderRadius: 10, background: 'var(--crm-surface)', color: 'var(--crm-text)', marginBottom: 14 };
const input: React.CSSProperties = { padding: 8, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', width: '100%', boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
const button: React.CSSProperties = { padding: '9px 12px', borderRadius: 'var(--crm-radius-md)', border: '1px solid var(--crm-info)', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', cursor: 'pointer' };
const dayKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const operatingContextIdentity = (context: ReturnType<typeof getOperatingContext>) =>
  `${context.token ?? ''}:${context.generation}:${context.status}:${context.groupId ?? ''}`;
function errorMessage(error: unknown) {
  if (axios.isAxiosError<{ message?: string; title?: string; errors?: Record<string, string[]> }>(error)) {
    const data = error.response?.data;
    const validation = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
    return data?.message || validation || data?.title || 'Запит не виконано. Перевірте з’єднання та оновіть дані перед повтором.';
  }
  return error instanceof Error ? error.message : 'Не вдалося виконати дію.';
}

export default function KitchenWorkPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOperatingContext();
  const { groupId: confirmedGroupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [kitchens, setKitchens] = useState<ProducingKitchen[]>([]);
  const [kitchenScope, setKitchenScope] = useState(() => searchParams.get('kitchenId') || 'all');
  const [targetKitchenId, setTargetKitchenId] = useState(() => searchParams.get('kitchenId') || '');
  const [orders, setOrders] = useState<EventOrder[]>([]);
  const [selected, setSelected] = useState<number[]>(() => {
    const id = Number(searchParams.get('orderId'));
    return id > 0 ? [id] : [];
  });
  const [plannedDate, setPlannedDate] = useState(dayKey(new Date()));
  const [from, setFrom] = useState(dayKey(new Date()));
  const [to, setTo] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingParameters, setEditingParameters] = useState<WorkPreviewItem | null>(null);
  const [parameters, setParameters] = useState<WorkParameters | null>(null);
  const [editingTask, setEditingTask] = useState<KitchenTask | null>(null);
  const [assignedUserId, setAssignedUserId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [notes, setNotes] = useState('');
  const [statusTask, setStatusTask] = useState<{ task: KitchenTask; status: KitchenTask['status'] } | null>(null);
  const [actual, setActual] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [taskDate, setTaskDate] = useState('');
  const previewSequence = useRef(0);
  const workspaceSequence = useRef(0);
  const actionSequence = useRef(0);
  const actionRunning = useRef(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<KitchenEmployee | null>(null);
  const [employeeStations, setEmployeeStations] = useState<string[]>([]);
  const [newStation, setNewStation] = useState('');
  const [removingStation, setRemovingStation] = useState<Station | null>(null);
  const [employeeCapacity, setEmployeeCapacity] = useState('');
  const [recommendationMessage, setRecommendationMessage] = useState('');
  const [historyTask, setHistoryTask] = useState<KitchenTask | null>(null);
  const [history, setHistory] = useState<TaskHistory[]>([]);
  const [refreshingTask, setRefreshingTask] = useState<KitchenTask | null>(null);

  const changedInAnotherTabRef = useRef(changedInAnotherTab);
  changedInAnotherTabRef.current = changedInAnotherTab;
  const invalidatedByStorageRef = useRef(false);
  const contextIdentityRef = useRef(operatingContextIdentity(context));

  const getContextSnapshot = useCallback((): OperatingContextSnapshot | null => {
    const current = getOperatingContext();
    const token = localStorage.getItem('token');
    if (!token || changedInAnotherTabRef.current || invalidatedByStorageRef.current
      || current.groupId !== confirmedGroupId || !isOperatingContextReady(current, token)
      || !current.groupId || localStorage.getItem(OPERATING_GROUP_STORAGE_KEY) !== current.groupId) return null;
    return { token, generation: current.generation, groupId: current.groupId };
  }, [confirmedGroupId]);

  const isContextSnapshotCurrent = useCallback((snapshot: OperatingContextSnapshot) => {
    const current = getOperatingContext();
    return !changedInAnotherTabRef.current && !invalidatedByStorageRef.current
      && isOperatingContextReady(current, snapshot.token)
      && current.generation === snapshot.generation && current.groupId === snapshot.groupId
      && localStorage.getItem(OPERATING_GROUP_STORAGE_KEY) === snapshot.groupId;
  }, []);

  const clearGroupState = useCallback(() => {
    ++workspaceSequence.current;
    ++previewSequence.current;
    ++actionSequence.current;
    actionRunning.current = false;
    setWorkspace(null);
    setKitchens([]);
    setKitchenScope('all');
    setTargetKitchenId('');
    setOrders([]);
    setSelected([]);
    setPlannedDate(dayKey(new Date()));
    setFrom(dayKey(new Date()));
    setTo('');
    setPreview(null);
    setPreviewLoading(false);
    setBusy(false);
    setError('');
    setMessage('');
    setEditingParameters(null);
    setParameters(null);
    setEditingTask(null);
    setAssignedUserId('');
    setDeadline('');
    setNotes('');
    setStatusTask(null);
    setActual('');
    setStatusNotes('');
    setEmployeeFilter('all');
    setStatusFilter('active');
    setTaskDate('');
    setEditingEmployee(null);
    setEmployeeStations([]);
    setNewStation('');
    setRemovingStation(null);
    setEmployeeCapacity('');
    setRecommendationMessage('');
    setHistoryTask(null);
    setHistory([]);
    setRefreshingTask(null);
    const scopedParams = new URLSearchParams(searchParams);
    scopedParams.delete('orderId');
    scopedParams.delete('kitchenId');
    if (scopedParams.toString() !== searchParams.toString()) setSearchParams(scopedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useLayoutEffect(() => {
    const contextChanged = () => {
      const current = getOperatingContext();
      const identity = operatingContextIdentity(current);
      if (identity === contextIdentityRef.current) return;
      contextIdentityRef.current = identity;
      if (current.status === 'group' || current.status === 'all') invalidatedByStorageRef.current = false;
      clearGroupState();
    };
    const storageChanged = (event: StorageEvent) => {
      const current = getOperatingContext();
      if (event.key !== 'token' && event.key !== null
        && !(event.key === OPERATING_GROUP_STORAGE_KEY && event.newValue !== current.groupId)) return;
      invalidatedByStorageRef.current = true;
      clearGroupState();
    };
    window.addEventListener(OPERATING_GROUP_CHANGED_EVENT, contextChanged);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener(OPERATING_GROUP_CHANGED_EVENT, contextChanged);
      window.removeEventListener('storage', storageChanged);
    };
  }, [clearGroupState]);

  const reload = useCallback(async (snapshot = getContextSnapshot()) => {
    if (!snapshot || !isContextSnapshotCurrent(snapshot)) return null;
    const sequence = ++workspaceSequence.current;
    const params = kitchenScope === 'all' ? undefined : kitchenScope === 'legacy' ? { unroutedOnly: true } : { kitchenId: kitchenScope };
    const response = params
      ? await api.get<Workspace>('/kitchenwork/workspace', { params })
      : await api.get<Workspace>('/kitchenwork/workspace');
    if (sequence === workspaceSequence.current && isContextSnapshotCurrent(snapshot)) setWorkspace(response.data);
    return sequence === workspaceSequence.current && isContextSnapshotCurrent(snapshot) ? response.data : null;
  }, [getContextSnapshot, isContextSnapshotCurrent, kitchenScope]);

  useEffect(() => {
    const snapshot = getContextSnapshot();
    if (!snapshot) return;
    let alive = true;
    (async () => {
      try {
        const [data, kitchensResponse] = await Promise.all([
          reload(snapshot), api.get<ProducingKitchen[]>('/producing-kitchens'),
        ]);
        if (!alive || !isContextSnapshotCurrent(snapshot) || !data) return;
        setKitchens(kitchensResponse.data);
        if (data.canManage) {
          const response = await api.get<EventOrder[]>('/eventorders', { params: { excludeCompleted: true } });
          if (alive && isContextSnapshotCurrent(snapshot)) setOrders(response.data);
        }
      } catch (err) { if (alive && isContextSnapshotCurrent(snapshot)) setError(errorMessage(err)); }
    })();
    return () => { alive = false; };
  }, [context.generation, context.groupId, context.status, context.token, getContextSnapshot, isContextSnapshotCurrent, reload]);

  const refreshPreview = useCallback(async (orderIds: number[], snapshot = getContextSnapshot(), isActive: () => boolean = () => true) => {
    if (!snapshot || !isContextSnapshotCurrent(snapshot)) return;
    const sequence = ++previewSequence.current;
    if (orderIds.length === 0) { setPreview(null); setPreviewLoading(false); return; }
    setPreviewLoading(true);
    try {
      const response = await api.post<Preview>('/kitchenwork/preview', {
        orderIds, kitchenId: targetKitchenId === 'legacy' ? null : Number(targetKitchenId),
      });
      if (isActive() && isContextSnapshotCurrent(snapshot) && sequence === previewSequence.current) setPreview(response.data);
    } finally {
      if (isActive() && isContextSnapshotCurrent(snapshot) && sequence === previewSequence.current) setPreviewLoading(false);
    }
  }, [getContextSnapshot, isContextSnapshotCurrent, targetKitchenId]);

  useEffect(() => {
    if (!workspace?.canManage) return;
    const snapshot = getContextSnapshot();
    if (!snapshot) return;
    ++previewSequence.current;
    setPreview(null);
    setPreviewLoading(selected.length > 0 && Boolean(targetKitchenId));
    if (!targetKitchenId) return;
    let active = true;
    const timer = window.setTimeout(() => {
      if (!isContextSnapshotCurrent(snapshot)) return;
      setError('');
      refreshPreview(selected, snapshot, () => active).catch(err => {
        if (active && isContextSnapshotCurrent(snapshot)) { setPreview(null); setError(errorMessage(err)); }
      });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [getContextSnapshot, isContextSnapshotCurrent, selected, targetKitchenId, workspace?.canManage, refreshPreview]);

  const run = async (action: (isCurrent: () => boolean, snapshot: OperatingContextSnapshot) => Promise<void>) => {
    if (actionRunning.current) return;
    const snapshot = getContextSnapshot();
    if (!snapshot) return;
    const sequence = ++actionSequence.current;
    const isCurrent = () => sequence === actionSequence.current && isContextSnapshotCurrent(snapshot);
    actionRunning.current = true;
    setBusy(true); setError(''); setMessage('');
    try { await action(isCurrent, snapshot); } catch (err) { if (isCurrent()) setError(errorMessage(err)); }
    finally {
      if (isCurrent()) { setBusy(false); actionRunning.current = false; }
    }
  };

  const createPlan = () => run(async (isCurrent, snapshot) => {
    if (!selected.length || !plannedDate || !targetKitchenId || !preview) throw new Error('Оберіть замовлення, кухню або явний legacy-режим і дату роботи.');
    const response = await api.post<{ created: number; reused: boolean }>('/kitchenwork/plans', {
      orderIds: selected, plannedDate, orderVersions: preview.orderVersions,
      kitchenId: targetKitchenId === 'legacy' ? null : Number(targetKitchenId),
      ...(targetKitchenId === 'legacy' ? { allowUnrouted: true } : {}),
    });
    if (!isCurrent()) return;
    await reload(snapshot);
    if (!isCurrent()) return;
    setTaskDate(plannedDate);
    setMessage(response.data.reused ? 'Такий план уже існує. Повторні завдання не створено.' : `План збережено. Завдань: ${response.data.created}. Тепер призначте відповідальних і дедлайни.`);
  });

  const saveParameters = () => run(async (isCurrent, snapshot) => {
    if (!editingParameters || !parameters) return;
    const validation = validateWorkParameters(parameters);
    if (validation) throw new Error(validation);
    await api.put(`/kitchenwork/parameters/${editingParameters.kind}/${editingParameters.id}`, parameters);
    if (!isCurrent()) return;
    await refreshPreview(selected, snapshot, isCurrent);
    if (!isCurrent()) return;
    setEditingParameters(null); setParameters(null);
    setMessage('Параметри збережено для наступних планів. Уже створені завдання залишаються зі своїм збереженим описом.');
  });

  const saveAssignment = () => run(async (isCurrent, snapshot) => {
    if (!editingTask) return;
    await api.put(`/kitchenwork/tasks/${editingTask.id}`, {
      version: editingTask.version, assignedUserId: assignedUserId || null,
      deadline: deadline ? new Date(deadline).toISOString() : null, notes,
    });
    if (!isCurrent()) return;
    await reload(snapshot);
    if (!isCurrent()) return;
    setEditingTask(null); setMessage('Призначення збережено.');
  });

  const saveEmployee = () => run(async (isCurrent, snapshot) => {
    if (!editingEmployee) return;
    const capacity = employeeCapacity === '' ? null : Number(employeeCapacity);
    if (capacity !== null && (!Number.isFinite(capacity) || capacity < 0 || capacity > 1440))
      throw new Error('Доступний час має бути від 0 до 1440 хвилин, або залиште невідомим.');
    const settingsPath = editingEmployee.employmentId
      ? `/kitchenwork/employments/${editingEmployee.employmentId}/parameters`
      : `/kitchenwork/employees/${encodeURIComponent(editingEmployee.id)}/parameters`;
    await api.put(settingsPath, {
      stations: employeeStations,
      availableMinutes: capacity,
    });
    if (!isCurrent()) return;
    await reload(snapshot);
    if (!isCurrent()) return;
    setEditingEmployee(null); setMessage('Напрями роботи й орієнтир доступного часу збережено.');
  });

  const saveStatus = () => run(async (isCurrent, snapshot) => {
    if (!statusTask) return;
    const quantity = Number(actual);
    if (['Review', 'Completed'].includes(statusTask.status) && (!actual || !Number.isFinite(quantity) || quantity <= 0 || quantity > statusTask.task.quantity))
      throw new Error('Вкажіть фактичну кількість більше нуля і не більше планової.');
    if (statusTask.status === 'Completed' && Math.abs(quantity - statusTask.task.quantity) > 0.000001)
      throw new Error('Закрити можна лише повністю виконаний план. Частковий результат поверніть у роботу для завершення решти.');
    if (statusTask.status === 'Blocked' && !statusNotes.trim()) throw new Error('Опишіть проблему.');
    await api.put(`/kitchenwork/tasks/${statusTask.task.id}/status`, {
      version: statusTask.task.version, status: statusTask.status,
      completedQuantity: ['Review', 'Completed'].includes(statusTask.status) ? quantity : undefined,
      notes: statusNotes,
    });
    if (!isCurrent()) return;
    await reload(snapshot);
    if (!isCurrent()) return;
    setStatusTask(null); setMessage('Стан роботи збережено. Складські залишки не змінювалися.');
  });

  const openStatus = (task: KitchenTask, status: KitchenTask['status']) => {
    setStatusTask({ task, status });
    setActual(String(task.completedQuantity || task.quantity));
    setStatusNotes(''); setError('');
  };
  const tasks = (workspace?.tasks ?? []).filter(task =>
    (!taskDate || task.plannedDate.slice(0, 10) === taskDate) &&
    (employeeFilter === 'all' || (employeeFilter === 'unassigned' ? !task.assignedUserId : task.assignedUserId === employeeFilter)) &&
    (statusFilter === 'all' || (statusFilter === 'active' ? !['Completed', 'Cancelled'].includes(task.status) : task.status === statusFilter)));
  const workload = getKitchenWorkload(tasks);
  const missingItems = preview?.items.filter(item => item.missing.length > 0) ?? [];
  const ordersInRange = orders.filter(order => selected.includes(order.id) || ((!from || order.eventDate.slice(0, 10) >= from) && (!to || order.eventDate.slice(0, 10) <= to)));
  const modal = (title: string, children: React.ReactNode, close: () => void, save: () => void, saveLabel = 'Зберегти') => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 1300, overflowY: 'auto', padding: 20 }}>
      <section role="dialog" aria-modal="true" aria-label={title} style={{ ...panel, maxWidth: 680, margin: '20px auto' }}>
        <h2>{title}</h2>
        {error && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{error}</p>}
        <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>{children}</fieldset>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button disabled={busy} style={button} onClick={save}>{busy ? 'Збереження…' : saveLabel}</button>
          <button disabled={busy} style={{ ...button, background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', borderColor: 'var(--crm-border)' }} onClick={close}>Скасувати</button>
        </div>
      </section>
    </div>
  );
  if (changedInAnotherTab) return <main id="kitchen-work-page" className="page-shell" style={{ padding: 20, color: 'var(--crm-text)' }}>
    <p role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p>
  </main>;
  return (
    <main id="kitchen-work-page" className="page-shell" style={{ padding: 20, color: 'var(--crm-text)' }}>
      <style>{`@media print {
        body * { visibility: hidden; }
        #kitchen-work-tasks, #kitchen-work-tasks * { visibility: visible; }
        #kitchen-work-tasks { position: absolute; left: 0; top: 0; width: 100%; }
        #kitchen-work-tasks button, #kitchen-work-tasks input, #kitchen-work-tasks select { display: none; }
        #kitchen-work-tasks article { break-inside: avoid; }
      }`}</style>
      <h1>{workspace?.canManage ? 'План кухні та розподіл роботи' : 'Мої завдання кухні'}</h1>
      <p>Підготовка, фінальне збирання страв і контроль виконання. Робочі відмітки не є складськими документами.</p>
      {workspace?.canManage && <section style={{ ...panel, borderLeft: '4px solid var(--crm-info)' }}>
        <h2 style={{ marginTop: 0 }}>Як користуватися — без повторного заповнення рецептур</h2>
        <p><strong>1. Обери замовлення</strong> → побачиш потрібні страви й н/ф, їхні операції та час із рецептур.</p>
        <p><strong>2. Уточни лише розподіл</strong> → станцію та активні хвилини працівника. Технологію й операції тут повторювати не потрібно.</p>
        <p><strong>3. Збережи план і признач виконавців</strong> → працівник починає роботу, повідомляє проблему або подає результат на перевірку.</p>
        <details><summary>Що означає час і одне завдання?</summary>
          <p>Приготування та видача підтягуються з рецептури як довідка. Запікання чи охолодження не завжди займають працівника весь цей час, тому активну працю підтверджуєш окремо.</p>
          <p>Одне завдання — вся страва або н/ф з переліком її операцій. Час між окремими операціями система не вигадує й поки не розподіляє їх між різними людьми автоматично.</p>
          <p>Дата роботи — день виконання, «Готово до» — твій термін. Готові залишки н/ф перевіряй перед плануванням: тут показана повна рецептурна потреба.</p>
        </details>
      </section>}
      <nav aria-label="Пов’язані розділи кухні" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Link to="/management/semi-finished-production" style={{ ...panel, marginBottom: 0, textDecoration: 'none', color: 'inherit', borderTop: '4px solid var(--crm-success)' }}>
          <strong style={{ display: 'block', color: 'var(--crm-success)', marginBottom: 6 }}>Виробництво н/ф →</strong>
          <span>Виробничі завдання та фіксація виготовлення</span>
        </Link>
        <Link to="/management/preparation-worksheets" style={{ ...panel, marginBottom: 0, textDecoration: 'none', color: 'inherit', borderTop: '4px solid var(--crm-info)' }}>
          <strong style={{ display: 'block', color: 'var(--crm-info)', marginBottom: 6 }}>Бланки підготовки →</strong>
          <span>Детальні операції та друк для кухні</span>
        </Link>
      </nav>
      {error && <div role="alert" style={{ ...panel, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderColor: 'var(--crm-danger)' }}>{error} <button style={button} disabled={busy} onClick={() => run(async () => { await reload(); })}>Оновити дані</button></div>}
      {message && <div role="status" style={{ ...panel, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)' }}>{message}</div>}
      {!workspace && !error && <p>Завантаження…</p>}
      {workspace?.canManage && !workspace.canEditManagement && <p role="note">Режим перегляду: для зміни плану кухні потрібне право редагування.</p>}
      {workspace?.canManage && (
        <section style={panel}>
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Станції кухні · {workspace.stations?.length ?? 0}</summary>
            <p>Спільний довідник для страв, н/ф і працівників. Змінювати довідник може лише системний адміністратор із правом редагування.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {workspace.stations?.map(station => <span key={station.id} style={{ padding: '6px 12px', borderRadius: 20, background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)' }}>
                {station.name}{station.isArchived ? ' · Архів' : ''}{' '}
                {workspace.canManageStationCatalog && (station.isArchived
                  ? <button disabled={busy} aria-label={`Відновити станцію ${station.name}`} onClick={() => run(async (isCurrent, snapshot) => {
                    await api.put(`/kitchenwork/stations/${station.id}/restore`);
                    if (!isCurrent()) return;
                    await reload(snapshot);
                    if (isCurrent()) setMessage('Станцію відновлено.');
                  })}>Відновити</button>
                  : <button disabled={busy} aria-label={`Видалити станцію ${station.name}`} onClick={() => { setRemovingStation(station); setError(''); }}>Видалити</button>)}
              </span>)}
              {!workspace.stations?.length && <span>Додайте першу станцію, наприклад «Холодний цех».</span>}
            </div>
            {workspace.canManageStationCatalog && <form onSubmit={e => { e.preventDefault(); run(async (isCurrent, snapshot) => {
              if (!newStation.trim()) throw new Error('Вкажіть назву станції.');
              await api.post('/kitchenwork/stations', { name: newStation.trim() });
              if (!isCurrent()) return;
              await reload(snapshot);
              if (!isCurrent()) return;
              setNewStation(''); setMessage('Станцію додано до спільного довідника.');
            }); }} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
              <label style={{ flex: '1 1 220px' }}>Нова станція / напрям<input style={input} maxLength={100} value={newStation} disabled={busy} onChange={e => setNewStation(e.target.value)} /></label>
              <button type="submit" disabled={busy || !newStation.trim()} style={button}>Додати станцію</button>
            </form>}
          </details>
        </section>
      )}
      {workspace?.canManage && (
        <section style={panel}>
          <h2>1. Оберіть замовлення</h2>
          <label>Кухня для формування робіт<select style={input} value={targetKitchenId} disabled={busy} onChange={e => setTargetKitchenId(e.target.value)}>
            <option value="">Оберіть кухню…</option>{kitchens.map(kitchen => <option key={kitchen.id} value={kitchen.id}>{kitchen.name}</option>)}
            <option value="legacy">Без маршрутизації — старий режим</option>
          </select></label>
          {targetKitchenId === 'legacy' && <p role="note">Буде сформовано лише явно обрані legacy/unrouted позиції.</p>}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label>Події від<input style={input} type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
            <label>Події до<input style={input} type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
            <label>Дата роботи<input style={input} type="date" value={plannedDate} disabled={busy} onChange={e => setPlannedDate(e.target.value)} /></label>
          </div>
          <div style={{ maxHeight: 250, overflowY: 'auto', margin: '12px 0' }}>
            {ordersInRange.map(order => <label key={order.id} style={{ display: 'block', padding: 8 }}>
              <input type="checkbox" disabled={busy} checked={selected.includes(order.id)} onChange={e => setSelected(ids => e.target.checked ? [...ids, order.id] : ids.filter(id => id !== order.id))} />
              {' '}#{order.id} · {order.eventDate.slice(0, 10)} · {order.customerName} · {order.eventName} · {order.eventVenue}
            </label>)}
            {!ordersInRange.length && <p>Немає відкритих замовлень у цьому діапазоні.</p>}
          </div>
          <p>Обрано: {selected.length}. <button disabled={busy || !selected.length} onClick={() => setSelected([])}>Очистити вибір</button></p>
          {previewLoading && <p role="status">Перевіряємо рецептури та параметри…</p>}
          {preview && <>
            <h2>2. Роботи з рецептур замовлень</h2>
            {preview.warnings.map((warning, index) => <p key={index} style={{ color: 'var(--crm-warning)' }}>{warning}</p>)}
            {missingItems.length > 0 && <div role="status" style={{ ...panel, background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)' }}>
              <strong>Бракує параметрів у {missingItems.length} картках.</strong> Натисніть «Заповнити» біля потрібної страви чи н/ф.
              Час без заповненої норми залишиться невідомим; це не заважає ручному призначенню.
            </div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
              {preview.items.map(item => <article key={`${item.kind}-${item.id}`} style={panel}>
                <strong>{item.kind === 'dish' ? 'Страва: ' : 'Н/Ф: '}{item.name}</strong>
                <p>{item.quantity.toFixed(3)} {item.unit} · {item.parameters.station || 'Станцію не вказано'}</p>
                {item.recipe && <>
                  <p><small>Із рецептури · база: {item.recipe.batchQuantity ?? 'вихід не вказано'} {item.unit}</small><br />
                    Приготування: {item.recipe.cookingMinutes == null ? 'не вказано' : `${Number(item.recipe.cookingMinutes.toFixed(2))} хв на базу`}<br />
                    Видача / викладка: {item.recipe.serveMinutes == null ? 'не вказано' : `${Number(item.recipe.serveMinutes.toFixed(2))} хв на базу`}
                  </p>
                  {item.recipe.batchQuantity != null && item.recipe.batchQuantity > 0 && <p>На це замовлення: приготування {item.recipe.cookingMinutes == null ? 'невідомо' : `${Math.round(item.recipe.cookingMinutes * item.quantity / item.recipe.batchQuantity)} хв`}; видача {item.recipe.serveMinutes == null ? 'невідомо' : `${Math.round(item.recipe.serveMinutes * item.quantity / item.recipe.batchQuantity)} хв`}. Це не підтверджені активні хвилини.</p>}
                  <details><summary>Операції та технологія з рецептури ({item.recipe.operations.length})</summary>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{item.recipe.instructions || 'Опис технології не внесено.'}</p>
                    {item.recipe.operations.map((operation, index) => <p key={index}>{operation}</p>)}
                    {!item.recipe.operations.length && <p>Операції компонентів не внесено в рецептуру.</p>}
                  </details>
                </>}
                <p>Активна праця: {item.parameters.activeMinutes == null ? 'ще не підтверджена' : `${item.parameters.activeMinutes} хв на ${item.parameters.batchQuantity} ${item.unit}`}</p>
                {item.missing.length > 0 ? <p style={{ color: 'var(--crm-warning)' }}>Не внесено: {item.missing.map(field => kitchenMissingLabels[field] || field).join(', ')}</p> : <p style={{ color: 'var(--crm-success)' }}>Готово до розподілу</p>}
                <Link to={item.kind === 'dish' ? '/management/dishes' : '/management/semi-finished'}>Рецептури: змінити операції або час</Link>
                <button disabled={busy || !workspace.canEditManagement} style={button} onClick={() => { setEditingParameters(item); setParameters({ ...item.parameters }); setError(''); }}>
                  {item.missing.length ? 'Заповнити тут' : 'Змінити параметри'}
                </button>
              </article>)}
            </div>
            <button disabled={busy || !workspace.canEditManagement || previewLoading || !preview.items.length || !targetKitchenId} style={{ ...button, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)' }} onClick={createPlan}>{busy ? 'Збереження…' : `Зберегти план (${preview.items.length} робіт)`}</button>
            <p><small>План фіксує опис і параметри. Повторний запуск для тих самих замовлень і дати не дублює роботи. Запаси поки не резервуються; перевірте потребу перед виробництвом.</small></p>
          </>}
        </section>
      )}
      <section id="kitchen-work-tasks" style={panel}>
        <h2>{workspace?.canManage ? '3. Призначення та виконання' : 'Виконання'}</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <label>Кухня завдань<select aria-label="Кухня завдань" style={input} value={kitchenScope} disabled={busy} onChange={e => setKitchenScope(e.target.value)}>
            <option value="all">Усі кухні й legacy</option><option value="legacy">Без виробничої кухні (legacy)</option>
            {kitchens.map(kitchen => <option key={kitchen.id} value={kitchen.id}>{kitchen.name}</option>)}
          </select></label>
          <label>Дата<input style={input} type="date" value={taskDate} onChange={e => setTaskDate(e.target.value)} /></label>
          <label>Стан<select style={input} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="active">Незавершені</option><option value="all">Усі</option>
            {Object.entries(workStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
          {workspace?.canManage && <label>Виконавець<select style={input} value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}>
            <option value="all">Усі</option><option value="unassigned">Не призначено</option>
            {workspace.employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
          </select></label>}
          <button style={button} disabled={busy} onClick={() => run(async () => { await reload(); })}>Оновити</button>
          <button style={{ ...button, background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', borderColor: 'var(--crm-border)' }} onClick={() => window.print()}>Друкувати</button>
        </div>
        <p>Незавершених: {workload.count} · Відомий активний час: {Math.round(workload.minutes)} хв · Без оцінки: {workload.unknown} · Проблем: {workload.blocked}</p>
        {workspace?.canManage && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {workspace.employees.map(employee => {
            const load = getKitchenWorkload((workspace.tasks ?? []).filter(task => task.assignedUserId === employee.id && (!taskDate || task.plannedDate.slice(0, 10) === taskDate)));
            return <div key={employee.id} style={{ padding: 10, border: '1px solid var(--crm-border)', borderRadius: 8, background: 'var(--crm-surface)' }}>
              <button onClick={() => setEmployeeFilter(employee.id)} style={{ background: 'none', border: 0, color: 'inherit', cursor: 'pointer' }}>
                {employee.name}: {load.count} робіт · {Math.round(load.minutes)} хв {load.unknown ? `+ ${load.unknown} без оцінки` : ''}
              </button>
              <div><small>{employee.stations?.join(', ') || 'Напрями не вказано'} · Орієнтир: {employee.availableMinutes == null ? 'час невідомий' : `${employee.availableMinutes} хв`}</small></div>
              <button disabled={busy || !workspace.canEditManagement || (employee.employmentId != null && !workspace.canEditEmploymentSettings)} onClick={() => {
                setEditingEmployee(employee); setEmployeeStations(employee.stations ?? []);
                setEmployeeCapacity(employee.availableMinutes == null ? '' : String(employee.availableMinutes)); setError('');
              }}>Напрями й доступний час</button>
            </div>;
          })}
        </div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {tasks.map(task => <article key={task.id} style={{ ...panel, borderLeft: `5px solid ${task.status === 'Blocked' ? 'var(--crm-danger)' : task.status === 'Completed' ? 'var(--crm-success)' : 'var(--crm-info)'}` }}>
            <strong>#{task.id} · {task.name}</strong>
            <p>{workStatusLabels[task.status]} · {task.quantity.toFixed(3)} {task.unit}</p>
            <p>{task.plannedDate.slice(0, 10)} · {task.station || 'Без станції'}<br />Виробнича кухня: {task.kitchenName || 'Без кухні — legacy'}<br />Виконавець: {task.assignedUserName || 'Не призначено'}</p>
            <p>Активний час: {task.estimatedMinutes === null ? 'не відомий' : `${Math.round(task.estimatedMinutes)} хв`}<br />Готово до: {task.deadline ? new Date(/(?:Z|[+-]\d{2}:\d{2})$/.test(task.deadline) ? task.deadline : `${task.deadline}Z`).toLocaleString('uk-UA') : 'не вказано'}</p>
            {!!task.historicalOrderIds?.length && <p role="note">Історична робота: меню замовлень {task.historicalOrderIds.map(id => `#${id}`).join(', ')} виправлено. Кількість, інструкції та прогрес цієї роботи збережено; вона не відповідає поточному меню.</p>}
            <details open><summary>Інструкція та замовлення</summary><p style={{ whiteSpace: 'pre-wrap' }}>{task.instructions || 'Інструкцію не внесено'}</p><p>Замовлення: {task.orderIds.map(id => `#${id}`).join(', ')}</p></details>
            {task.notes && <p style={{ whiteSpace: 'pre-wrap' }}>{task.notes}</p>}
            {task.completedQuantity > 0 && <p>Фактично: {task.completedQuantity.toFixed(3)} {task.unit}</p>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <button disabled={busy} style={button} onClick={() => run(async isCurrent => {
                const response = await api.get<TaskHistory[]>(`/kitchenwork/tasks/${task.id}/history`);
                if (!isCurrent()) return;
                setHistory(response.data); setHistoryTask(task);
              })}>Історія</button>
              {workspace?.canEditManagement && !['Completed', 'Cancelled'].includes(task.status) && <button disabled={busy} style={button} onClick={() => {
                setEditingTask(task); setAssignedUserId(task.assignedUserId || '');
                const due = task.deadline ? new Date(/(?:Z|[+-]\d{2}:\d{2})$/.test(task.deadline) ? task.deadline : `${task.deadline}Z`) : null;
                setDeadline(due ? `${dayKey(due)}T${String(due.getHours()).padStart(2, '0')}:${String(due.getMinutes()).padStart(2, '0')}` : '');
                setNotes(task.notes); setError(''); setRecommendationMessage('');
              }}>Призначити / змінити</button>}
              {workspace?.canEditManagement && !['Completed', 'Cancelled'].includes(task.status) && <button disabled={busy} onClick={() => { setRefreshingTask(task); setError(''); }}>Оновити з рецептури</button>}
              {(task.canExecute ?? !workspace?.canManage) && ['Planned', 'Blocked'].includes(task.status) && <button disabled={busy} style={button} onClick={() => openStatus(task, 'InProgress')}>Почати</button>}
              {(task.canExecute ?? !workspace?.canManage) && ['Planned', 'InProgress'].includes(task.status) && <button disabled={busy} style={{ ...button, background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', borderColor: 'var(--crm-warning)' }} onClick={() => openStatus(task, 'Blocked')}>Проблема</button>}
              {(task.canExecute ?? !workspace?.canManage) && ['Planned', 'InProgress', 'Blocked'].includes(task.status) && <button disabled={busy} style={{ ...button, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)' }} onClick={() => openStatus(task, 'Review')}>На перевірку</button>}
              {workspace?.canEditManagement && task.status === 'Review' && <>
                <button disabled={busy} style={{ ...button, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderColor: 'var(--crm-success)' }} onClick={() => openStatus(task, 'Completed')}>Прийняти</button>
                <button disabled={busy} style={button} onClick={() => openStatus(task, 'InProgress')}>Повернути в роботу</button>
              </>}
              {workspace?.canEditManagement && !['Completed', 'Cancelled'].includes(task.status) && <button disabled={busy} style={{ ...button, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderColor: 'var(--crm-danger)' }} onClick={() => openStatus(task, 'Cancelled')}>Скасувати</button>}
            </div>
          </article>)}
        </div>
        {workspace && !tasks.length && <p>Немає завдань за цими фільтрами.</p>}
      </section>
      {removingStation && modal(`Видалення станції: ${removingStation.name}`, <>
        <p>Невикористану станцію буде видалено. Якщо вона вже є у стравах, н/ф, працівниках або завданнях, її буде архівовано зі збереженням налаштувань та історії.</p>
        <p>Архівна станція недоступна для нових призначень. Її можна відновити в довіднику.</p>
      </>, () => setRemovingStation(null), () => run(async (isCurrent, snapshot) => {
        const response = await api.delete<{ archived: boolean }>(`/kitchenwork/stations/${removingStation.id}`);
        if (!isCurrent()) return;
        await reload(snapshot);
        if (!isCurrent()) return;
        setRemovingStation(null);
        setMessage(response.data.archived ? 'Станцію архівовано. Наявні налаштування й історію збережено.' : 'Невикористану станцію видалено.');
      }), 'Підтвердити видалення')}
      {editingParameters && parameters && modal(`Параметри: ${editingParameters.name}`, <>
        <p>Операції, спосіб обробки, температура й технологія вже беруться з рецептури. Тут задаємо лише правила розподілу роботи.</p>
        <p>{editingParameters.kind === 'dish' ? 'Норма рецептурного часу — на 1 порцію. Активна праця страви — без повторного виготовлення її н/ф.' : 'База підтягується з виходу рецептури н/ф. Активна праця — лише власний процес без виготовлення вкладених н/ф.'}</p>
        <label>Станція / напрям *<select style={input} value={parameters.station} onChange={e => setParameters({ ...parameters, station: e.target.value })}>
          <option value="">Оберіть станцію</option>
          {workspace?.stations?.filter(station => !station.isArchived || station.name === editingParameters.parameters.station)
            .map(station => <option key={station.id} value={station.name}>{station.name}{station.isArchived ? ' (архів)' : ''}</option>)}
        </select></label>
        {!workspace?.stations?.length && <p>Спочатку додайте напрям у розділі «Станції кухні» на цій сторінці.</p>}
        <label>Базова партія ({editingParameters.unit}) *<input style={input} type="number" min="0.001" step="0.001" value={parameters.batchQuantity ?? ''} onChange={e => setParameters({ ...parameters, batchQuantity: e.target.value === '' ? null : Number(e.target.value) })} /></label>
        <label>Активна робота на партію (хв)<input style={input} type="number" min="0" step="0.1" value={parameters.activeMinutes ?? ''} onChange={e => setParameters({ ...parameters, activeMinutes: e.target.value === '' ? null : Number(e.target.value) })} /></label>
        {editingParameters.recipe && <div style={panel}>
          <p>У рецептурі: приготування {editingParameters.recipe.cookingMinutes ?? 'невідомо'} хв; видача {editingParameters.recipe.serveMinutes ?? 'невідомо'} хв на {editingParameters.recipe.batchQuantity ?? '?'} {editingParameters.unit}.</p>
          {editingParameters.recipe.batchQuantity != null && editingParameters.recipe.batchQuantity > 0 &&
            editingParameters.recipe.cookingMinutes != null && editingParameters.recipe.serveMinutes != null &&
            <button type="button" onClick={() => setParameters({
              ...parameters,
              batchQuantity: editingParameters.recipe?.batchQuantity ?? null,
              activeMinutes: (editingParameters.recipe?.cookingMinutes ?? 0) + (editingParameters.recipe?.serveMinutes ?? 0),
              passiveMinutes: 0,
            })}>Підтверджую: весь вказаний час — активна праця</button>}
          <p><small>Якщо є пасивне очікування, введи активні хвилини вручну. Відсутній час не означає нуль; перевір обидва етапи перед підтвердженням. Можна залишити активний час порожнім і призначати вручну.</small></p>
        </div>}
        <label>Очікування на партію (хв)<input style={input} type="number" min="0" step="0.1" value={parameters.passiveMinutes ?? ''} onChange={e => setParameters({ ...parameters, passiveMinutes: e.target.value === '' ? null : Number(e.target.value) })} /></label>
        <label>Додаткова примітка до плану (необов’язково)<textarea rows={3} style={input} value={parameters.instructions} onChange={e => setParameters({ ...parameters, instructions: e.target.value })} /></label>
        <p><small>Це окремі параметри планування. Рецептура, ціни й складські залишки не змінюються. Очікування не додається до зайнятості працівника.</small></p>
      </>, () => { setEditingParameters(null); setParameters(null); }, saveParameters)}
      {refreshingTask && modal(`Оновлення з рецептури: ${refreshingTask.name}`, <>
        <p>Замінити збережені операції, технологію, станцію та оцінку часу актуальними даними рецептури й правилами розподілу?</p>
        <p>Кількість, виконавець, термін і стан виконання залишаться незмінними. Зміна буде записана в історію.</p>
      </>, () => setRefreshingTask(null), () => run(async (isCurrent, snapshot) => {
        await api.put(`/kitchenwork/tasks/${refreshingTask.id}/recipe`, { version: refreshingTask.version });
        if (!isCurrent()) return;
        await reload(snapshot);
        if (!isCurrent()) return;
        setRefreshingTask(null); setMessage('Завдання оновлено з рецептури. Призначення та кількість збережено.');
      }), 'Оновити з рецептури')}
      {editingTask && modal(`Призначення: ${editingTask.name}`, <>
        <button style={button} onClick={() => {
          const suggestion = recommendKitchenAssignee(editingTask, workspace?.employees ?? [], workspace?.tasks ?? []);
          setRecommendationMessage(suggestion.reason);
          if (suggestion.employee) setAssignedUserId(suggestion.employee.id);
        }}>Запропонувати за станцією та навантаженням</button>
        {recommendationMessage && <p role="status">{recommendationMessage} Призначення зміниться лише після збереження.</p>}
        <label>Відповідальний<select style={input} value={assignedUserId} onChange={e => setAssignedUserId(e.target.value)}>
          <option value="">Не призначено</option>{workspace?.employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
        </select></label>
        <label>Готово до<input style={input} type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} /></label>
        <label>Примітка<textarea style={input} rows={4} value={notes} onChange={e => setNotes(e.target.value)} /></label>
      </>, () => setEditingTask(null), saveAssignment)}
      {editingEmployee && modal(`Параметри працівника: ${editingEmployee.name}`, <>
        <fieldset style={{ border: '1px solid var(--crm-border)', borderRadius: 8 }}>
          <legend>Дозволені станції / напрями</legend>
          {workspace?.stations?.filter(station => !station.isArchived || editingEmployee.stations?.includes(station.name))
            .map(station => <label key={station.id} style={{ display: 'block', padding: 6 }}>
            <input type="checkbox" checked={employeeStations.includes(station.name)} onChange={e => setEmployeeStations(values => e.target.checked ? [...values, station.name] : values.filter(value => value !== station.name))} /> {station.name}{station.isArchived ? ' (архів — наявне налаштування)' : ''}
          </label>)}
          {!workspace?.stations?.length && <p>Спочатку додайте станції в спільний довідник.</p>}
        </fieldset>
        <label>Орієнтир доступного активного часу на день (хв)<input style={input} type="number" min="0" max="1440" value={employeeCapacity} onChange={e => setEmployeeCapacity(e.target.value)} /></label>
        <p>Оберіть лише напрями, які працівник може виконувати самостійно. Страви й н/ф використовують той самий довідник.</p>
        <p>Це базовий орієнтир, не графік зміни. Перевіряйте реальну присутність, перерви, обладнання й дедлайни перед затвердженням. Порожній час означає «невідомо».</p>
      </>, () => setEditingEmployee(null), saveEmployee)}
      {statusTask && modal(`${workStatusLabels[statusTask.status]}: ${statusTask.task.name}`, <>
        {['Review', 'Completed'].includes(statusTask.status) && <label>Фактична кількість ({statusTask.task.unit})<input style={input} type="number" min="0" max={statusTask.task.quantity} step="any" value={actual} onChange={e => setActual(e.target.value)} /></label>}
        {statusTask.status === 'Completed' && <p>Прийняття закриває весь план: {statusTask.task.quantity} {statusTask.task.unit}. Якщо виконано лише частину, поверніть роботу виконавцю.</p>}
        <label>{statusTask.status === 'Blocked' ? 'Що заважає виконанню *' : 'Коментар'}<textarea style={input} rows={4} value={statusNotes} onChange={e => setStatusNotes(e.target.value)} /></label>
        <p>Відмітка виконання не створює прихід н/ф і не списує сировину.</p>
      </>, () => setStatusTask(null), saveStatus)}
      {historyTask && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 1300, overflowY: 'auto', padding: 20 }}>
        <section role="dialog" aria-modal="true" aria-label={`Історія: ${historyTask.name}`} style={{ ...panel, maxWidth: 680, margin: '20px auto' }}>
          <h2>Історія: {historyTask.name}</h2>
          {history.map(event => <article key={event.id} style={panel}>
            <strong>{new Date(event.occurredAtUtc.endsWith('Z') ? event.occurredAtUtc : `${event.occurredAtUtc}Z`).toLocaleString('uk-UA')} · {event.actor}</strong>
            <p>{event.fromStatus ? workStatusLabels[event.fromStatus] : 'Створення'} → {workStatusLabels[event.toStatus]}</p>
            {event.completedQuantity != null && <p>Фактично: {event.completedQuantity} {historyTask.unit}</p>}
            {event.notes && <p style={{ whiteSpace: 'pre-wrap' }}>{event.notes}</p>}
          </article>)}
          {!history.length && <p>Записів немає.</p>}
          <button style={button} onClick={() => setHistoryTask(null)}>Закрити</button>
        </section>
      </div>}
    </main>
  );
}
